import { Component, OnInit } from '@angular/core';
import { LeadService } from '../../../../../../shared/store/lead/lead.service';
import { filter, Observable, take } from 'rxjs';
import { CarInsuranceLeadModel } from '../../../../../../shared/store/lead/types';
import {
  AbstractControl,
  ControlContainer,
  FormArray,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { formatDate } from '../../../../../../shared/helper/datehelper';
import { Entity } from '../../../../../../shared/store/view/types';
import {
  DataType,
  DropdownStyle,
} from '../../../../../../shared/store/typeahead/types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  removeValidators,
  setValidators,
} from 'src/app/shared/helper/formhelper';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../../../../shared/constants/date.constants';

@UntilDestroy()
@Component({
  selector: 'app-lead-form-driver',
  templateUrl: './lead-form-driver.component.html',
  styleUrls: ['./lead-form-driver.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class LeadFormDriverComponent implements OnInit {
  leadForm: FormGroup<any>;
  carDetail$: Observable<CarInsuranceLeadModel>;

  protected readonly Entity = Entity;
  protected readonly DropdownStyle = DropdownStyle;
  protected readonly DataType = DataType;

  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly leadService: LeadService,
    private parent: FormGroupDirective,
    private formBuilder: FormBuilder,
  ) {
    this.carDetail$ = this.leadService.getCarDetail.value$;
  }

  ngOnInit(): void {
    this.leadForm = this.parent.form;

    this.leadForm.addControl(
      'driverFormGroup',
      this.formBuilder.group({
        driverGender: [undefined, [Validators.required]],
        driverLastname: [''],
        driverFirstname: [''],
        driverBirthdate: [undefined, [Validators.required]],
        driverZipCode: [''],
        driverCity: [undefined],
        driverAddress: [''],
        driverMaritalStatus: [undefined, [Validators.required]],
        driverLicenseDate: [undefined, [Validators.required]],
        driverYearsInsured: [undefined],
        driverBonusMalus: [undefined],
        driverMonthInsuranceInterruption: [undefined],
        driverInsuranceTermination: [undefined],
        driverLicenseAnnulation: [false],
        driverNoLicenseConviction: [false],
        driverLicenseCancel: [false],
        driverLicenseCancelMotive: [undefined],
        driverLicenseCancelDate: [undefined],
        driverLicenseSuspension: [false],
        driverLicenseSuspensionMotive: [undefined],
        driverLicenseSuspensionDate: [undefined],
        driverLongestSuspension: [undefined],
        driverAlcoholDrugControl: [false],
        driverVehicleRegistrationHolder: [true],
        isSameContactChecked: [true],
        driverDisasters: this.formBuilder.array([]),
      }),
    );

    this.carDetail$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.leadForm.patchValue({
          driverFormGroup: {
            driverGender: x.driverGender,
            driverLastname: x.driverLastName,
            driverFirstname: x.driverFirstName,
            driverBirthdate: formatDate(x.driverBirthdate, 'ToInverseSqlDate'),
            driverZipCode: x.driverZipCode,
            driverCity: x.driverCity,
            driverAddress: x.driverAddress,
            driverMaritalStatus: x.driverMaritalStatus,
            driverLicenseDate: formatDate(x.licenseDate, 'ToInverseSqlDate'),
            driverYearsInsured: x.yearsInsured,
            driverBonusMalus: x.bonusMalus,
            driverMonthInsuranceInterruption: x.monthInsuranceInterruption,
            driverInsuranceTermination: x.insuranceTermination,
            driverLicenseAnnulation: x.licenseAnnulation,
            driverNoLicenseConviction: x.noLicenseConviction,
            driverLicenseCancel: x.licenseCancel,
            driverLicenseCancelMotive: x.licenseCancelMotive,
            driverLicenseCancelDate: formatDate(
              x.licenseCancelDate,
              'ToInverseSqlDate',
            ),
            driverLicenseSuspension: x.licenseSuspension,
            driverLicenseSuspensionMotive: x.licenseSuspensionMotive,
            driverLicenseSuspensionDate: formatDate(
              x.licenseSuspensionDate,
              'ToInverseSqlDate',
            ),
            driverLongestSuspension: x.longestSuspension,
            driverAlcoholDrugControl: x.alcoholDrugControl,
            driverVehicleRegistrationHolder:
              x.isDriverVehiculeRegistrationHolder,
          },
        });

        const disasters = x.carDisasters;
        var dataDisaster: any[] = [];
        for (let i = 0; i < disasters.length; i++) {
          this.addDisaster();
          dataDisaster.push({
            carDisasterReason: disasters[i].carDisasterReason,
            carDisasterResponsiblePerson:
              disasters[i].carDisasterResponsiblePerson,
            carDisasterDate: formatDate(
              disasters[i].carDisasterDate,
              'ToInverseSqlDate',
            ),
          });
        }
        this.driverDisasters.patchValue(dataDisaster);
      });

    this.leadForm.controls['driverFormGroup']
      .get('isSameContactChecked')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value == false) {
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverLastname'),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverFirstname'),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverCity'),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.minLength(5),
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.maxLength(5),
          );
        } else {
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverLastname'),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverFirstname'),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverCity'),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.minLength(5),
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get('driverZipCode'),
            Validators.maxLength(5),
          );
        }
      });

    this.leadForm.controls['driverFormGroup']
      .get('driverLicenseSuspension')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value == false || value == undefined) {
          this.leadForm.controls['driverFormGroup']
            .get('driverLicenseSuspensionMotive')
            ?.setValue(undefined);
          this.leadForm.controls['driverFormGroup']
            .get('driverLicenseSuspensionDate')
            ?.setValue(undefined);
          this.leadForm.controls['driverFormGroup']
            .get('driverLongestSuspension')
            ?.setValue(undefined);

          removeValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseSuspensionMotive',
            ),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseSuspensionDate',
            ),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLongestSuspension',
            ),
            Validators.required,
          );
        } else {
          setValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseSuspensionMotive',
            ),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseSuspensionDate',
            ),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLongestSuspension',
            ),
            Validators.required,
          );
        }
      });

    this.leadForm.controls['driverFormGroup']
      .get('driverLicenseCancel')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value == false || value == undefined) {
          this.leadForm.controls['driverFormGroup']
            .get('driverLicenseCancelMotive')
            ?.setValue(undefined);
          this.leadForm.controls['driverFormGroup']
            .get('driverLicenseCancelDate')
            ?.setValue(undefined);

          removeValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseCancelMotive',
            ),
            Validators.required,
          );
          removeValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseCancelDate',
            ),
            Validators.required,
          );
        } else {
          setValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseCancelMotive',
            ),
            Validators.required,
          );
          setValidators(
            this.leadForm.controls['driverFormGroup'].get(
              'driverLicenseCancelDate',
            ),
            Validators.required,
          );
        }
      });
  }

  get driverDisasters() {
    let driverFormGroup = this.leadForm.controls[
      'driverFormGroup'
    ] as FormGroup;

    return driverFormGroup.controls['driverDisasters'] as FormArray;
  }

  addDisaster() {
    const disasterForm = this.formBuilder.group({
      carDisasterDate: [undefined, [Validators.required]],
      carDisasterReason: [undefined, [Validators.required]],
      carDisasterResponsiblePerson: [undefined, [Validators.required]],
    });

    this.driverDisasters.push(disasterForm);
  }

  removeDisaster(index: number) {
    this.driverDisasters.removeAt(index);
  }

  getCarDisasterReasonControl(i: number): AbstractControl {
    return (this.leadForm.get('driverFormGroup.driverDisasters') as FormArray)
      .at(i)
      .get('carDisasterReason')!;
  }

  getCarDisasterResponsibleControl(i: number): AbstractControl {
    return (this.leadForm.get('driverFormGroup.driverDisasters') as FormArray)
      .at(i)
      .get('carDisasterResponsiblePerson')!;
  }
}
